import React, { useContext, useMemo } from "react";

import { Stack, Paper, TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import {
  useListOrganisations,
  useListUsers,
  useListClients,
} from "../../../store";

import { Organisation, UserType } from "../../../types";
import { userHasPermission } from "../../../types-helpers";

import { createUserDictionary } from "../../../utils/create-dictionary";
import AdminTeamsTable from "./admin-teams-table";

type AdminTeamsTabProps = {
  selectedClientId?: string;
};

const AdminTeamsTab: React.FC<AdminTeamsTabProps> = ({ selectedClientId }) => {
  const { data: clients = [], isLoading: clientsLoading } = useListClients();

  const { data: teams = [], isLoading: teamsLoading } =
    useListOrganisations(selectedClientId);
  const { data: users = [], isLoading: usersLoading } = useListUsers(
    true,
    selectedClientId,
  );

  const userDictionary = useMemo(() => {
    return createUserDictionary(users);
  }, [users]);

  const [searchTerm, setSearchTerm] = React.useState("");
  const [sortBy, setSortBy] = React.useState<string>("name");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("asc");
  const [currentPage, setCurrentPage] = React.useState(0);
  const [rowsPerPage] = React.useState(10);

  React.useEffect(() => {
    setCurrentPage(0);
  }, [selectedClientId, searchTerm]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const getOrganisationUserCount = (orgId: string) => {
    return users.filter((user) => user.organisationIds.includes(orgId)).length;
  };

  const getOrganisationAdminCount = (orgId: string) => {
    return users.filter(
      (user) =>
        user.organisationIds.includes(orgId) &&
        (userHasPermission(UserType.SysAdmin, user) ||
          user.adminIds.includes(orgId)),
    ).length;
  };

  const sortTeams = (teams: Organisation[]) => {
    return [...teams].sort((a, b) => {
      let aValue: any = a[sortBy as keyof Organisation];
      let bValue: any = b[sortBy as keyof Organisation];

      if (sortBy === "created") {
        if (!aValue && !bValue) return 0;
        if (!aValue) return 1;
        if (!bValue) return -1;
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (sortBy === "userCount") {
        aValue = getOrganisationUserCount(a.organisationId);
        bValue = getOrganisationUserCount(b.organisationId);
      } else if (sortBy === "adminCount") {
        aValue = getOrganisationAdminCount(a.organisationId);
        bValue = getOrganisationAdminCount(b.organisationId);
      } else if (sortBy === "status") {
        aValue =
          getOrganisationUserCount(a.organisationId) > 0
            ? "active"
            : "inactive";
        bValue =
          getOrganisationUserCount(b.organisationId) > 0
            ? "active"
            : "inactive";
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      } else if (sortBy === "clientId") {
        aValue = aValue?.toLowerCase() || "";
        bValue = bValue?.toLowerCase() || "";
      } else if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue?.toLowerCase() || "";
      }

      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  };

  let filteredTeams = teams.filter(
    (org) =>
      org.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      org.organisationId?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  filteredTeams = sortTeams(filteredTeams);

  return (
    <Stack spacing={2}>
      {/* Teams Table with Tab-style Filters */}
      <Paper>
        {/* Filters */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 1 }}>
          <TextField
            placeholder="Search teams by name or ID..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flexGrow: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
        <AdminTeamsTable
          teamsLoading={teamsLoading}
          usersLoading={usersLoading}
          clientsLoading={clientsLoading}
          filteredTeams={filteredTeams}
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          searchTerm={searchTerm}
          userDictionary={userDictionary}
        />
      </Paper>
    </Stack>
  );
};

export default AdminTeamsTab;
